/*
 *
 * File: ChooseCarCategoryOffer.jsx
 *
 * Purpose: Choose offer component.
 *
 * Author:  Thomas Burgard (Software development)
 * Created: 23.02.2022
 *
 * Copyright (c) DrivePartner GmbH 2022
 *
 */

import { React, useState } from "react";
import { useTranslation } from "react-i18next";
import * as constants from "../../utils/constants";
import { carCategory } from "../../utils/dataObjects";

//--------------------------------------------------------------
//--- CSS styles -----------------------------------------------
//--------------------------------------------------------------
const carCategoryBoxStyleNormal = {
  border: "1px solid #ced4da",
  borderRadius: "5px",
  padding: "20px",
  paddingLeft: "10px",
  paddingRight: "10px",
  marginBottom: "15px",
  backgroundColor: "#fafafa",
  cursor: "pointer",
};
const carCategoryBoxStyleSelected = {
  border: "2px solid #4d5db5",
  borderRadius: "5px",
  padding: "20px",
  paddingLeft: "10px",
  paddingRight: "10px",
  marginBottom: "15px",
  backgroundColor: "#edefff",
};
const carCategoryTextStyle = {
  fontSize: "1.1rem",
  fontWeight: "bold",
};
const carCategoryFareStyle = {
  fontSize: "1.1rem",
  fontWeight: "bold",
  textAlign: "right",
};
const carCategoryModelsStyle = {
  fontSize: "0.9rem",
};
const tableStyle = {
  border: "0px",
  width: "100%",
};
//--- End CSS styles -------------------------------------------

/**
 * Implementation of choose car category offer component. Show all
 * configured car category fare information in the
 * booking screen.
 *
 * @param {*} props (userData,
 *                   calcFaresFinished,
 *                   userCalcRouteObj,
 *                   userRegistrationDataObj,
 *                   choosenCarOffer)
 * @returns JSX-HTML-DOM
 */
const ChooseCarCategoryOffer = (props) => {
  const { t, i18n } = useTranslation("common");
  const [chooseCarCategoryBoxIndex, setChooseCarCategoryBoxIndex] = useState(0);

  /**
   * Show car category fare.
   *
   * @param {*} carCategory car category
   * @returns JSX for car category fare
   */
  const showCarCategoryFare = (carCategory) => {
    let carCategoryFare;

    if (carCategory === constants.LIMOUSINE_BUSINESS) {
      carCategoryFare = props.userCalcRouteObj.calcFareLimBusiness;
    } else if (carCategory === constants.LIMOUSINE_FIRSTCLASS) {
      carCategoryFare = props.userCalcRouteObj.calcFareLimFirstClass;
    } else if (carCategory === constants.BUSINESS_VAN) {
      carCategoryFare = props.userCalcRouteObj.calcFareBusinessVan;
    } else if (carCategory === constants.BUS_XL) {
      carCategoryFare = props.userCalcRouteObj.calcFareBusXL;
    }

    return carCategoryFare;
  };

  /**
   * Show car category models.
   *
   * @param {*} carCategory car category object
   * @returns JSX for car category models
   */
  const showCarCategoryModels = (carCategoryObj) => {
    if (carCategoryObj.MODEL1 !== "") {
      if (carCategoryObj.MODEL2 !== "") {
        if (carCategoryObj.MODEL3 !== "") {
          if (carCategoryObj.MODEL4 !== "") {
            return (
              <>
                {carCategoryObj.MODEL1}, {carCategoryObj.MODEL2},{" "}
                {carCategoryObj.MODEL3}, {carCategoryObj.MODEL4}{" "}
              </>
            );
          } else {
            return (
              <>
                {carCategoryObj.MODEL1}, {carCategoryObj.MODEL2},{" "}
                {carCategoryObj.MODEL3}{" "}
              </>
            );
          }
        } else {
          return (
            <>
              {carCategoryObj.MODEL1}, {carCategoryObj.MODEL2}{" "}
            </>
          );
        }
      } else {
        return <>{carCategoryObj.MODEL1} </>;
      }
    }

    return null;
  };

  /**
   * Car category box click handler.
   *
   * @param {*} selBoxIndex Selected car category box index
   * @param {*} carCategory Car category (car offer)
   */
  const carCategoryBoxClickHandler = (selBoxIndex, carCategory) => {
    setChooseCarCategoryBoxIndex(selBoxIndex);
    props.choosenCarOffer(carCategory); // Call function in parent component with given car category (car offer)!
  };

  /**
   * Set the selected box style.
   * 
   * @param {*} selBoxIndex 
   * @returns box CSS style
   */
  const setStyle = (selBoxIndex) => {
    if (selBoxIndex === chooseCarCategoryBoxIndex) {
      return carCategoryBoxStyleSelected;
    } else {
      return carCategoryBoxStyleNormal;
    }
  };

  /**
   * Show car category boxes with calculated fare
   * value, car category models & other infos.
   *
   * @returns JSX for car category calculation values box
   */
  const showCarCategories = () => {
    if (props.userData.driverCars !== false) {
      return props.userData.driverCars.map((element, index) => (
        <div
          style={setStyle(index)}
          onClick={() => carCategoryBoxClickHandler(index, element.CARCATEGORY)}
        >
          <table style={tableStyle}>
            <tr>
              <td style={carCategoryTextStyle}>
                {element.CARCATEGORY_ALTERNATIVE_NAME === null ||
                element.CARCATEGORY_ALTERNATIVE_NAME === ""
                  ? element.CARCATEGORY
                  : element.CARCATEGORY_ALTERNATIVE_NAME}
              </td>
              <td style={carCategoryFareStyle}>
                € {showCarCategoryFare(element.CARCATEGORY)}.00
              </td>
            </tr>
          </table>
          <p style={{ paddingLeft: "3px", color: "#666666" }}>
            <span style={carCategoryModelsStyle}>
              {showCarCategoryModels(element)}
            </span>
          </p>
        </div>
      ));
    } else {
      return null;
    }
  };

  //--------------------------------------------------------------
  //--- Returns JSX code for "ChooseCarCategoryOffer" ------------
  //--------------------------------------------------------------
  return (
    <>
      {props.calcFaresFinished && props.userRegistrationDataObj.tariff === 2 ? (
        <>
          <div
            style={{
              fontSize: "1.2rem",
              fontWeight: "bold",
              marginTop: "30px",
              marginBottom: "15px",
            }}
          >
            {t("formular.header-choose-offer")}
          </div>
          {showCarCategories()}
        </>
      ) : null}
    </>
  );
};

export default ChooseCarCategoryOffer;
